import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, combineLatest, of, switchMap } from 'rxjs';
import { EmployeeDropdownResponse, EmployeeService } from '@core/employee/employee.service';
import { SecurityRoleDropdownResponse, SecurityRoleService } from '@core/security/security-role.service';
import { SharedModule } from '@shared/shared.module';

export interface AssigneeSelection {
	selectedPersonIds: number[];
	requireAll: boolean;
	roleIds: number[];
}

@Component({
	selector: 'pms-task-assignee-picker',
	templateUrl: './task-assignee-picker.component.html',
	styleUrls: ['./task-assignee-picker.component.scss'],
	standalone: true,
	imports: [CommonModule, FormsModule, SharedModule],
})
export class TaskAssigneePickerComponent implements OnInit {
	private employeeService = inject(EmployeeService);
	private securityRoleService = inject(SecurityRoleService);
	private destroyRef = inject(DestroyRef);

	private _employees: EmployeeDropdownResponse[] = [];
	private roleFilterChange$ = new Subject<void>();

	@Input() initialSelectedPersonIds: number[] = [];
	@Input() initialRequireAll = false;
	@Input() initialRoleIds: number[] = [];

	@Output() selectionChanged = new EventEmitter<AssigneeSelection>();

	roles: SecurityRoleDropdownResponse[] = [];
	roleFields = { text: 'label', value: 'value' };
	selectedRoleIds: number[] = [];
	searchText = '';
	displayedEmployees: EmployeeDropdownResponse[] = [];
	selectedEmployeeIds = new Set<number>();
	requireAll = false;
	isLoading = true;

	ngOnInit() {
		this.requireAll = this.initialRequireAll;
		this.selectedRoleIds = this.initialRoleIds;

		combineLatest([
			this.employeeService.findActiveEmployeesForDropdown(),
			this.securityRoleService.findSecurityRolesForDropdown(),
		]).subscribe(([employees, roles]) => {
			this._employees = employees;
			this.selectedEmployeeIds = new Set(
				this.initialSelectedPersonIds
					.map(personId => employees.find(employee => employee.personId === personId)?.employeeId)
					.filter((id): id is number => id != null),
			);
			this.roles = roles;
			this.applySearchFilter();
			this.isLoading = false;
		});

		this.roleFilterChange$.pipe(
			switchMap(() => this.selectedRoleIds?.length > 0
				? this.employeeService.findActiveEmployeeIdsBySecurityRoleIds(this.selectedRoleIds)
				: of([])),
			takeUntilDestroyed(this.destroyRef),
		).subscribe(employeeIds => {
			this.selectedEmployeeIds = new Set(employeeIds);
			this.emitSelection();
		});
	}

	private applySearchFilter() {
		if (this.searchText?.trim()) {
			const search = this.searchText.trim().toLowerCase();
			this.displayedEmployees = this._employees.filter(employee =>
				employee.label.toLowerCase().includes(search),
			);
		} else {
			this.displayedEmployees = this._employees;
		}
	}

	onRoleFilterChange() {
		this.selectedEmployeeIds.clear();
		this.roleFilterChange$.next();
	}

	onSearchChange() {
		this.applySearchFilter();
	}

	clearSearch() {
		this.searchText = '';
		this.applySearchFilter();
	}

	isSelected(employee: EmployeeDropdownResponse): boolean {
		return this.selectedEmployeeIds.has(employee.employeeId);
	}

	toggleEmployee(employee: EmployeeDropdownResponse) {
		if (this.selectedEmployeeIds.has(employee.employeeId)) {
			this.selectedEmployeeIds.delete(employee.employeeId);
		} else {
			this.selectedEmployeeIds.add(employee.employeeId);
		}
		this.emitSelection();
	}

	get allVisibleSelected(): boolean {
		return this.displayedEmployees.length > 0
			&& this.displayedEmployees.every(employee => this.selectedEmployeeIds.has(employee.employeeId));
	}

	toggleSelectAll() {
		if (this.allVisibleSelected) {
			this.displayedEmployees.forEach(employee => this.selectedEmployeeIds.delete(employee.employeeId));
		} else {
			this.displayedEmployees.forEach(employee => this.selectedEmployeeIds.add(employee.employeeId));
		}
		this.emitSelection();
	}

	onRequireAllChange() {
		this.emitSelection();
	}

	private emitSelection() {
		const selectedPersonIds = Array.from(this.selectedEmployeeIds)
			.map(employeeId => this._employees.find(employee => employee.employeeId === employeeId)?.personId)
			.filter((id): id is number => id != null);
		this.selectionChanged.emit({
			selectedPersonIds,
			requireAll: this.requireAll,
			roleIds: this.selectedRoleIds,
		});
	}
}
